import { useState, ReactNode } from "react";
import classNames from "classnames";
import {
  cleanParagraphTags,
  GetBackgroundStyle,
  IsNodeStructure,
} from "@talladega/helpers";
import {
  AcfIconPosition,
  ActionButton,
  CmsButtonType,
  FullWidthCTABannerLayout,
} from "@talladega/types";
import { CmsHeading } from "../CmsHeading";

type Props = {
  layout: FullWidthCTABannerLayout;

  cmsButton: (
    setButtonModalState: React.Dispatch<
      React.SetStateAction<{ show: boolean; actionType: ActionButton }>
    >,
    button: CmsButtonType,
    htmlIdRef: React.MutableRefObject<HTMLDivElement | null> | undefined
  ) => ReactNode;

  ctaIconImage: (
    bannerIndex: number,
    imageProps: {
      src?: string;
      alt?: string;
      width?: number;
      height?: number;
    }
  ) => ReactNode;

  cmsButtonModalContainer: (state: {
    show: boolean;
    actionType: ActionButton;
  }) => ReactNode;

  baseDomain: string;
};

export const FullWidthCTABanner = ({
  layout,
  cmsButton,
  ctaIconImage,
  cmsButtonModalContainer,
  baseDomain,
}: Props) => {
  const {
    fullWidthCtaBannerGroup: { heading, subheading, ctaBannerList, background },
    htmlIdRef,
    isStandalone,
  } = layout;

  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  return (
    <div
      id="cms-full-width-cta-banner"
      className={classNames("no-scrollbar relative overflow-hidden", {
        "px-4 pb-6 pt-10 lg:px-5 lg:py-16": isStandalone,
      })}
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="mx-auto max-w-page">
        <article className="px-4 py-8 lg:px-5">
          <div className="md:text-center">
            {heading && (
              <CmsHeading
                headingLevel={heading.headingGroup?.headingLevel}
                textSize={heading.headingGroup?.textSize}
                text={heading.headingGroup?.text}
                htmlId={heading.headingGroup?.htmlId}
              />
            )}
            {subheading && (
              <div
                className={classNames({
                  "mt-4 lg:mt-8": heading?.headingGroup?.text,
                })}
              >
                <CmsHeading
                  headingLevel={subheading.headingGroup?.headingLevel}
                  textSize={subheading.headingGroup?.textSize}
                  text={subheading.headingGroup?.text}
                  htmlId={subheading.headingGroup?.htmlId}
                />
              </div>
            )}
          </div>
        </article>

        <div className="my-8 flex flex-col md:flex-row items-center justify-center gap-y-14 gap-x-20">
          {ctaBannerList?.map((banner, index) => {
            const {
              ctaIcon,
              ctaHeading,
              ctaDescription,
              ctaIconPosition,
              button,
            } = banner;

            const { altText, sourceUrl, mediaDetails } =
              (IsNodeStructure(ctaIcon) ? ctaIcon.node : ctaIcon) ?? {};

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`cta-banner-${index}`}>
                <div className="flex flex-col items-center justify-center">
                  <div
                    className={classNames("flex gap-x-4", {
                      "flex-col items-center":
                        ctaIconPosition === AcfIconPosition.TopCenter,
                      "flex-col items-start":
                        ctaIconPosition === AcfIconPosition.TopLeft,
                      "flex-row items-start":
                        ctaIconPosition === AcfIconPosition.Left,
                    })}
                  >
                    {ctaIconImage(index, {
                      src: sourceUrl,
                      alt: altText,
                      width: mediaDetails?.width,
                      height: mediaDetails?.height,
                    })}

                    {(ctaHeading || ctaDescription) && (
                      <div className="flex flex-col">
                        {ctaHeading && (
                          <h3
                            className="text-xl font-semibold"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: cleanParagraphTags(ctaHeading),
                            }}
                          />
                        )}
                        {ctaDescription && (
                          <p
                            className="text-base font-normal"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: cleanParagraphTags(ctaDescription),
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {!button.isHidden && (
                    <div className="mx-auto w-56 pt-4">
                      {cmsButton(setButtonModalState, button, htmlIdRef)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {buttonModalState.show && cmsButtonModalContainer(buttonModalState)}
    </div>
  );
};

export default {};
